import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { DiamondCard } from '../components/DiamondCard';
import { DiamondLoader } from '../components/DiamondLoader';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { toast } from 'sonner';

export const DiamondCatalog = () => {
  const { user, isAuthenticated } = useAuth();
  const [diamonds, setDiamonds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    shape: 'all',
    colour: 'all',
    clarity: 'all',
    cut: 'all',
    lab: 'all',
    shade: 'all',
    stock_id: '',
    ratio: '',
    minSize: '',
    maxSize: '',
    minPrice: '',
    maxPrice: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const canViewPrice = isAuthenticated && (user?.status === 'approved' || user?.role === 'admin');

  useEffect(() => {
    fetchDiamonds();
  }, [canViewPrice]);

  const fetchDiamonds = async () => {
    try {
      const response = canViewPrice 
        ? await api.getDiamonds()
        : await api.getPublicDiamonds();
      setDiamonds(response.data);
    } catch (error) {
      console.error('Failed to fetch diamonds:', error);
      toast.error('Failed to load diamonds');
    } finally {
      setLoading(false);
    }
  };

  const filteredDiamonds = diamonds.filter(diamond => {
    // Search filter
    const matchesSearch = searchTerm === '' || 
      diamond.shape.toLowerCase().includes(searchTerm.toLowerCase()) ||
      diamond.colour.toLowerCase().includes(searchTerm.toLowerCase()) ||
      diamond.clarity.toLowerCase().includes(searchTerm.toLowerCase()) ||
      diamond.certificate.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (diamond.stock_id && diamond.stock_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (diamond.shade && diamond.shade.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (diamond.ratio && diamond.ratio.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Shape filter
    const matchesShape = filters.shape === 'all' || diamond.shape === filters.shape;
    
    // Color filter
    const matchesColour = filters.colour === 'all' || diamond.colour === filters.colour;
    
    // Clarity filter
    const matchesClarity = filters.clarity === 'all' || diamond.clarity === filters.clarity;
    
    // Cut filter
    const matchesCut = filters.cut === 'all' || diamond.cut === filters.cut;
    
    // Lab filter
    const matchesLab = filters.lab === 'all' || diamond.lab === filters.lab;
    
    // Shade filter
    const matchesShade = filters.shade === 'all' || (diamond.shade && diamond.shade === filters.shade);
    
    // Stock ID filter
    const matchesStockId = !filters.stock_id || (diamond.stock_id && diamond.stock_id.toLowerCase().includes(filters.stock_id.toLowerCase()));
    
    // Ratio filter
    const matchesRatio = !filters.ratio || (diamond.ratio && diamond.ratio.toLowerCase().includes(filters.ratio.toLowerCase()));
    
    // Size filter
    const matchesMinSize = !filters.minSize || diamond.size >= parseFloat(filters.minSize);
    const matchesMaxSize = !filters.maxSize || diamond.size <= parseFloat(filters.maxSize);
    
    // Price filter (only if user can view price)
    const matchesMinPrice = !canViewPrice || !filters.minPrice || diamond.total_amount >= parseFloat(filters.minPrice);
    const matchesMaxPrice = !canViewPrice || !filters.maxPrice || diamond.total_amount <= parseFloat(filters.maxPrice);
    
    return matchesSearch && matchesShape && matchesColour && matchesClarity && 
           matchesCut && matchesLab && matchesShade && matchesStockId && matchesRatio &&
           matchesMinSize && matchesMaxSize && matchesMinPrice && matchesMaxPrice;
  });

  const shapes = ['all', ...new Set(diamonds.map(d => d.shape))];
  const colours = ['all', ...new Set(diamonds.map(d => d.colour))];
  const clarities = ['all', ...new Set(diamonds.map(d => d.clarity))];
  const cuts = ['all', ...new Set(diamonds.map(d => d.cut))];
  const labs = ['all', ...new Set(diamonds.map(d => d.lab))];
  const shades = ['all', ...new Set(diamonds.filter(d => d.shade).map(d => d.shade))];

  const resetFilters = () => {
    setFilters({
      shape: 'all',
      colour: 'all',
      clarity: 'all',
      cut: 'all',
      lab: 'all',
      shade: 'all',
      stock_id: '',
      ratio: '',
      minSize: '',
      maxSize: '',
      minPrice: '',
      maxPrice: ''
    });
    setSearchTerm('');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <DiamondLoader size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 md:px-12 lg:px-24 py-12">
      <div className="mb-12" data-testid="catalog-header">
        <h1 className="font-heading text-4xl md:text-5xl font-semibold mb-4">Diamond Catalog</h1>
        {!canViewPrice && (
          <div className="bg-primary/10 border border-primary px-6 py-4 mb-8">
            <p className="text-sm">
              <span className="font-semibold">Register to view pricing.</span> Create an account to access full diamond details and competitive pricing.
            </p>
          </div>
        )}
      </div>

      {/* Search and Filter Toggle */}
      <div className="mb-8 space-y-4">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by shape, color, clarity, or certificate..."
              className="w-full bg-white border border-border focus:border-primary outline-none pl-12 pr-4 py-3 transition-colors"
              data-testid="catalog-search-input"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="bg-white border border-border hover:border-primary px-6 py-3 flex items-center gap-2 transition-colors"
            data-testid="toggle-filters-btn"
          >
            <SlidersHorizontal size={18} />
            Filters
          </button>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="bg-white border border-border p-6 space-y-6" data-testid="advanced-filters">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold">Advanced Filters</h3>
              <button
                onClick={resetFilters}
                className="text-sm text-muted-foreground hover:text-primary flex items-center gap-2"
              >
                <X size={16} />
                Reset All
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Shape</label>
                <select
                  value={filters.shape}
                  onChange={(e) => setFilters({...filters, shape: e.target.value})}
                  className="w-full bg-white border border-border focus:border-primary outline-none px-4 py-2"
                >
                  {shapes.map(shape => (
                    <option key={shape} value={shape}>{shape === 'all' ? 'All Shapes' : shape}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Color</label>
                <select
                  value={filters.colour}
                  onChange={(e) => setFilters({...filters, colour: e.target.value})}
                  className="w-full bg-white border border-border focus:border-primary outline-none px-4 py-2"
                >
                  {colours.map(colour => (
                    <option key={colour} value={colour}>{colour === 'all' ? 'All Colors' : colour}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Clarity</label>
                <select
                  value={filters.clarity}
                  onChange={(e) => setFilters({...filters, clarity: e.target.value})}
                  className="w-full bg-white border border-border focus:border-primary outline-none px-4 py-2"
                >
                  {clarities.map(clarity => (
                    <option key={clarity} value={clarity}>{clarity === 'all' ? 'All Clarities' : clarity}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Cut</label>
                <select
                  value={filters.cut}
                  onChange={(e) => setFilters({...filters, cut: e.target.value})}
                  className="w-full bg-white border border-border focus:border-primary outline-none px-4 py-2"
                >
                  {cuts.map(cut => (
                    <option key={cut} value={cut}>{cut === 'all' ? 'All Cuts' : cut}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Lab</label>
                <select
                  value={filters.lab}
                  onChange={(e) => setFilters({...filters, lab: e.target.value})}
                  className="w-full bg-white border border-border focus:border-primary outline-none px-4 py-2"
                >
                  {labs.map(lab => (
                    <option key={lab} value={lab}>{lab === 'all' ? 'All Labs' : lab}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Shade</label>
                <select
                  value={filters.shade}
                  onChange={(e) => setFilters({...filters, shade: e.target.value})}
                  className="w-full bg-white border border-border focus:border-primary outline-none px-4 py-2"
                >
                  {shades.map(shade => (
                    <option key={shade} value={shade}>{shade === 'all' ? 'All Shades' : shade}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Stock ID</label>
                <input
                  type="text"
                  placeholder="Search by Stock ID"
                  value={filters.stock_id}
                  onChange={(e) => setFilters({...filters, stock_id: e.target.value})}
                  className="w-full bg-white border border-border focus:border-primary outline-none px-4 py-2"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Ratio</label>
                <input
                  type="text"
                  placeholder="Search by Ratio"
                  value={filters.ratio}
                  onChange={(e) => setFilters({...filters, ratio: e.target.value})}
                  className="w-full bg-white border border-border focus:border-primary outline-none px-4 py-2"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border">
              <div>
                <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Size Range (Carat)</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Min"
                    value={filters.minSize}
                    onChange={(e) => setFilters({...filters, minSize: e.target.value})}
                    className="w-full bg-white border border-border focus:border-primary outline-none px-4 py-2"
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Max"
                    value={filters.maxSize}
                    onChange={(e) => setFilters({...filters, maxSize: e.target.value})}
                    className="w-full bg-white border border-border focus:border-primary outline-none px-4 py-2"
                  />
                </div>
              </div>

              {canViewPrice && (
                <div>
                  <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Price Range ($)</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.minPrice}
                      onChange={(e) => setFilters({...filters, minPrice: e.target.value})}
                      className="w-full bg-white border border-border focus:border-primary outline-none px-4 py-2"
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.maxPrice}
                      onChange={(e) => setFilters({...filters, maxPrice: e.target.value})}
                      className="w-full bg-white border border-border focus:border-primary outline-none px-4 py-2"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="mb-6">
        <p className="text-sm text-muted-foreground">
          Showing <span className="font-semibold text-foreground">{filteredDiamonds.length}</span> of <span className="font-semibold text-foreground">{diamonds.length}</span> diamonds
        </p>
      </div>

      {/* Diamond Grid */}
      {filteredDiamonds.length === 0 ? (
        <div className="text-center py-24" data-testid="no-diamonds-message">
          <p className="text-muted-foreground text-lg">No diamonds found matching your criteria.</p>
          <button
            onClick={resetFilters}
            className="mt-4 text-primary hover:underline font-semibold"
          >
            Clear all filters
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="diamond-grid">
          {filteredDiamonds.map(diamond => (
            <DiamondCard key={diamond.diamond_id} diamond={diamond} showPrice={canViewPrice} />
          ))}
        </div>
      )}
    </div>
  );
};